// =============================================================================
// VandieZ shipper-web -- Wallet (/wallet)
// =============================================================================
// Balance, pending payouts, ledger feed + Razorpay top-up flow.
//
// Top-up: POST /users/me/wallet/recharge returns either:
//   - qr_image_url=null  → dev/test mode, recharge already credited; just
//                          refresh balance.
//   - qr_image_url       → Razorpay UPI QR; user scans + pays; we poll
//                          GET /recharge/{id} until status='paid'.
// =============================================================================

import {
  AlertCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  Download,
  Loader2,
  Plus,
  Wallet,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import type { WalletRechargeResponse } from "@/api/client";
import { walletApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { ApiError } from "@/types/auth";
import type { LedgerEntry, LedgerEntryType, WalletSummary } from "@/types/wallet";

const TYPE_LABEL: Record<LedgerEntryType, string> = {
  topup: "Top-up",
  trip_charge: "Trip charge",
  platform_commission: "Platform commission",
  refund: "Refund",
  payout_initiated: "Payout initiated",
  payout_settled: "Payout settled",
  payout_failed: "Payout failed",
  adjustment: "Adjustment",
};

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatSignedINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  const sign = n > 0 ? "+" : "";
  return `${sign}₹${Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function exportLedgerCsv(entries: LedgerEntry[]): void {
  const header = [
    "id",
    "entry_type",
    "amount",
    "trip_id",
    "payout_id",
    "notes",
    "created_at",
  ];
  const escape = (v: string): string =>
    /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  const csv = [
    header.join(","),
    ...entries.map((e) =>
      [
        e.id,
        e.entry_type,
        e.amount,
        e.trip_id ?? "",
        e.payout_id ?? "",
        e.notes ?? "",
        e.created_at,
      ]
        .map((c) => escape(String(c)))
        .join(","),
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vandiez-wallet-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function WalletPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [data, setData] = useState<WalletSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const refresh = (signal?: AbortSignal): Promise<void> => {
    if (accessToken === null) {return Promise.resolve();}
    return walletApi
      .me(accessToken, signal)
      .then((s) => {
        setData(s);
        setError(null);
      })
      .catch((err: unknown) => {
        if (signal?.aborted === true) {return;}
        setError(
          err instanceof ApiError ? err.problem.detail : "Couldn't load wallet.",
        );
      })
      .finally(() => { setIsLoading(false); });
  };

  useEffect(() => {
    if (accessToken === null) {return;}
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallet"
        description="Balance, pending payouts, and recent ledger activity."
        actions={
          <Button onClick={() => { setTopUpOpen(true); }}>
            <Plus className="mr-1 h-4 w-4" />
            Top up
          </Button>
        }
      />

      {topUpOpen && (
        <TopUpModal
          onClose={() => { setTopUpOpen(false); }}
          onPaid={() => {
            setTopUpOpen(false);
            void refresh();
          }}
        />
      )}

      <>
        {error !== null ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data === null ? null : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wallet className="h-4 w-4" />
                  Balance
                </CardTitle>
                <CardDescription>
                  Current available balance plus payouts already in flight.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    Available
                  </p>
                  <p className="text-3xl font-semibold tabular-nums">
                    {formatINR(data.balance)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    Pending payouts
                  </p>
                  <p className="text-3xl font-semibold tabular-nums text-muted-foreground">
                    {formatINR(data.pending_payouts_total)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-col gap-2 space-y-0 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                <CardTitle className="text-base">Recent activity</CardTitle>
                <CardDescription>
                  {data.entries_total === 0
                    ? "No activity yet."
                    : `${data.entries.length} of ${data.entries_total} most recent entries.`}
                </CardDescription>
                </div>
                {data.entries.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { exportLedgerCsv(data.entries); }}
                    className="self-start sm:self-auto"
                  >
                    <Download className="mr-1 h-4 w-4" />
                    CSV
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                {data.entries.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Wallet activity appears here once you complete a trip or
                    top up your balance.
                  </p>
                ) : (
                  data.entries.map((e) => <LedgerRow key={e.id} entry={e} />)
                )}
              </CardContent>
            </Card>
          </>
        )}
      </>
    </div>
  );
}

function LedgerRow({ entry }: { entry: LedgerEntry }): React.JSX.Element {
  const positive = Number.parseFloat(entry.amount) >= 0;
  return (
    <div className="flex items-center justify-between rounded-md border bg-background px-4 py-3">
      <div className="flex items-center gap-3">
        {positive ? (
          <ArrowDownCircle className="h-5 w-5 text-success" />
        ) : (
          <ArrowUpCircle className="h-5 w-5 text-danger" />
        )}
        <div>
          <p className="text-sm font-medium">{TYPE_LABEL[entry.entry_type]}</p>
          <p className="text-xs text-muted-foreground">
            {formatDate(entry.created_at)}
            {entry.trip_id !== null ? (
              <>
                {" · "}
                <Link
                  to={`/trips/${entry.trip_id}`}
                  className="text-primary hover:underline"
                >
                  trip
                </Link>
              </>
            ) : null}
          </p>
          {entry.notes !== null && entry.notes.length > 0 ? (
            <p className="text-xs text-muted-foreground">{entry.notes}</p>
          ) : null}
        </div>
      </div>
      <p
        className={`text-sm font-mono tabular-nums ${positive ? "text-success-subtle-foreground" : "text-danger-subtle-foreground"}`}
      >
        {formatSignedINR(entry.amount)}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Razorpay top-up modal
// ---------------------------------------------------------------------------
// Three states the user can be in:
//   1. Idle      -> typing an amount.
//   2. QR shown  -> server returned a Razorpay QR; we poll every 3s for status.
//   3. Paid      -> show success + onPaid callback refreshes the wallet.
// Test mode (no Razorpay creds on the backend) skips step 2 — qr_image_url
// is null and the recharge is already credited, so we go straight to step 3.
function TopUpModal({
  onClose,
  onPaid,
}: {
  onClose: () => void;
  onPaid: () => void;
}): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [amount, setAmount] = useState<string>("1000");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [order, setOrder] = useState<WalletRechargeResponse | null>(null);
  const [status, setStatus] = useState<
    "idle" | "polling" | "paid" | "expired" | "failed"
  >("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (accessToken === null) {return;}
    const n = Number.parseInt(amount, 10);
    if (!Number.isFinite(n) || n < 200) {
      setError("Minimum top-up is ₹200.");
      return;
    }
    if (n > 100_000) {
      setError("Maximum top-up is ₹100,000.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await walletApi.recharge(accessToken, n);
      setOrder(res);
      if (res.qr_image_url === null) {
        // Test-mode: backend credited inline; nothing more to do.
        setStatus("paid");
        toast.success(`Wallet credited ₹${n}.`);
      } else {
        setStatus("polling");
      }
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.problem.detail : "Couldn't create the order.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Poll the order status every 3s while we're waiting on payment.
  useEffect(() => {
    if (status !== "polling" || order === null || accessToken === null) {return;}
    const intervalId = window.setInterval(async () => {
      try {
        const s = await walletApi.rechargeStatus(accessToken, order.recharge_id);
        if (s.status === "paid") {
          setStatus("paid");
          toast.success(`Wallet credited ₹${order.amount}.`);
        } else if (s.status === "expired" || s.status === "failed") {
          setStatus(s.status);
        }
      } catch {
        // Transient errors are fine — keep polling.
      }
    }, 3000);
    return () => { window.clearInterval(intervalId); };
  }, [status, order, accessToken, toast]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Top up wallet
          </CardTitle>
          <CardDescription>
            {order === null
              ? "Pre-fund your wallet so you can settle trips online instead of cash."
              : status === "paid"
                ? "Recharge credited. Your balance updates on close."
                : status === "expired"
                  ? "The QR expired before payment. Try again."
                  : status === "failed"
                    ? "The payment failed. Try again or pick a different UPI app."
                    : "Scan the QR with any UPI app. We'll detect the payment automatically."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {order === null ? (
            <div className="space-y-2">
              <Label htmlFor="topup-amount">Amount (₹200 – ₹100,000)</Label>
              <Input
                id="topup-amount"
                inputMode="numeric"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); }}
                disabled={submitting}
              />
            </div>
          ) : order.qr_image_url !== null && status === "polling" ? (
            <div className="flex flex-col items-center gap-2">
              <img
                src={order.qr_image_url}
                alt="Razorpay UPI QR"
                className="h-64 w-64 object-contain"
              />
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Waiting for payment…
              </div>
            </div>
          ) : status === "paid" ? (
            <div className="flex flex-col items-center gap-2 py-4 text-success-subtle-foreground">
              <CheckCircle2 className="h-10 w-10" />
              <div className="text-sm font-medium">Top-up successful</div>
            </div>
          ) : null}

          {error !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Stack buttons on phones, side-by-side at sm+. flex-col-reverse
              keeps the primary CTA on top of the stack (visual priority). */}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            {status === "paid" ? (
              <Button onClick={onPaid}>Done</Button>
            ) : order === null ? (
              <>
                <Button variant="ghost" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button onClick={() => void submit()} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Generate QR"
                  )}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
